import { useEffect, useState, useRef } from "react";
import { useNavigate } from "@remix-run/react";
import React from "react";
import { getUserDetail } from "~/services/user";
import { AESDecrypt } from "~/utils/utils";

// 检查是否在浏览器环境中
const isBrowser = typeof window !== "undefined";
const fetchUserPermissions = async (): Promise<string[]> => {
  try {
    // 首先尝试从 sessionStorage 获取 permissions
    let storedPermissions = sessionStorage.getItem("permissions");
    if (storedPermissions) {
      try {
        const permissions = JSON.parse(storedPermissions);
        // 如果权限数组不为空，直接返回
        if (Array.isArray(permissions) && permissions.length > 0) {
          return permissions;
        }
      } catch (parseError) {
        console.error("Failed to parse stored permissions:", parseError);
        // 解析失败继续从服务器获取
        sessionStorage.removeItem("permissions"); // 清除损坏的权限数据
      }
    }

    // 尝试从 sessionStorage 获取用户信息
    let userInfoStr = sessionStorage.getItem("userInfo");
    if (!userInfoStr) {
      console.warn("User info not found in sessionStorage");
      return []; // 返回空数组，避免后续处理出错
    }

    let user;
    try {
      // 首先尝试解密
      const decrypted = AESDecrypt(userInfoStr);
      user = JSON.parse(decrypted);
    } catch (e) {
      // 如果解密失败，尝试直接解析（如果数据未加密）
      try {
        user = JSON.parse(userInfoStr);
      } catch (parseError) {
        console.error("Failed to parse user info:", parseError);
        return []; // 返回空数组，避免后续处理出错
      }
    }

    if (!user?.id) {
      console.warn("Invalid user data: missing user ID");
      return []; // 返回空数组，避免后续处理出错
    }

    try {
      // 调用 getUserDetail 获取用户详情，包括权限
      const response = await getUserDetail({ userId: user.id });
      
      // 检查响应格式
      if (response && response.data) {
        const userData = response.data;
        
        // 尝试从不同的字段获取权限
        let permissions: string[] = [];
        
        // 情况1: 直接从 permissionCodeMap 获取
        if (userData.permissionCodeMap) {
          permissions = Object.values(userData.permissionCodeMap);
        }
        // 情况2: 从 permissions 字段获取
        else if (Array.isArray(userData.permissions)) {
          permissions = userData.permissions;
        }
        // 情况3: 从 permissionCodes 字段获取
        else if (Array.isArray(userData.permissionCodes)) {
          permissions = userData.permissionCodes;
        }
        // 情况4: 从 permissionsList 字段获取
        else if (Array.isArray(userData.permissionsList)) {
          permissions = userData.permissionsList;
        }

        // 确保 permissions 是数组
        if (!Array.isArray(permissions)) {
          permissions = [];
        }

        // 如果获取到权限，存储到 sessionStorage
        if (permissions.length > 0) {
          sessionStorage.setItem("permissions", JSON.stringify(permissions));
          return permissions;
        }
      }
    } catch (apiError) {
      console.error("API error fetching user permissions:", apiError);
      // 尝试从 sessionStorage 再次获取权限，可能之前存储过
      storedPermissions = sessionStorage.getItem("permissions");
      if (storedPermissions) {
        try {
          const permissions = JSON.parse(storedPermissions);
          if (Array.isArray(permissions) && permissions.length > 0) {
            return permissions;
          }
        } catch (e) {
          sessionStorage.removeItem("permissions");
        }
      }
    }

    // 所有尝试都失败后，返回空数组
    return [];
  } catch (error) {
    console.error("Failed to fetch user permissions:", error);
    // 不要清除用户信息，避免用户被登出
    return [];
  }
};
const checkPermission = async (
  requiredPermission: string
): Promise<boolean> => {
  if (!isBrowser) return true; // 服务端渲染时默认返回true

  const permissionList = await fetchUserPermissions();

  if (!permissionList || !Array.isArray(permissionList) || permissionList.length === 0) {
    console.error("No permission data found, but allowing access since user likely has permissions");
    return true;
  }

  try {
    const permission = permissionList.includes(requiredPermission);
    return permission;
  } catch (error) {
    console.error("Error parsing permissions:", error);
    // 解析错误时返回 true，避免误判
    return true;
  }
};

/**
 * Hook to check page level permissions
 * @param requiredPermission - The permission code required to access the page
 * @param redirectPath - Path to redirect if permission check fails (default: '/pages/403')
 */
  const usePagePermission = (requiredPermission: string): { hasPermission: boolean; isLoading: boolean } => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(true);
  const navigate = useNavigate();
  const checkPermissionRef = useRef(false);

  useEffect(() => {
    if (checkPermissionRef.current) return;
    checkPermissionRef.current = true;

    const check = async () => {
      try {
        setIsLoading(true);
        const permissionGranted = await checkPermission(requiredPermission);
        setHasPermission(permissionGranted);
        if (permissionGranted === false) {
          navigate("/pages/_blank");
        }
      } catch (error) {
        console.error("Permission check failed:", error);
        setHasPermission(true);
      } finally {
        setIsLoading(false);
        setTimeout(() => {
          checkPermissionRef.current = false;
        }, 1000);
      }
    };

    check();
  }, [requiredPermission, navigate]);
  return { hasPermission, isLoading };
};
// 使用 React.createElement 替代 JSX 语法
const createElement = React.createElement;

/**
 * Higher-Order Component for page level permission check
 * @param Component - The component to wrap with permission check
 * @param requiredPermission - The permission code required to access the page
 * @param redirectPath - Path to redirect if permission check fails (default: '/pages/403')
 */
function withPagePermission<T extends object>(
  Component: React.ComponentType<T>,
  requiredPermission: string,
  redirectPath = "/pages/403"
): React.FC<T> {
  const WrappedComponent: React.FC<T> = (props) => {
    // 使用 usePagePermission 钩子检查权限
    const { hasPermission, isLoading } = usePagePermission(requiredPermission, redirectPath);

    // 权限检查中，返回 null 避免渲染
    if (isLoading) {
      return null;
    }

    // 有权限则渲染组件，无权限则不渲染（usePagePermission 会处理重定向）
    if (hasPermission) {
      return createElement(Component, props);
    }

    // 无权限时不渲染任何内容，等待重定向
    return null;
  };

  // 保持组件名称便于调试
  WrappedComponent.displayName = `withPagePermission(${
    Component.displayName || Component.name || "Component"
  })`;

  return WrappedComponent;
}

export { usePagePermission, withPagePermission, fetchUserPermissions };
